import { useCallback, useEffect, useState } from 'react';
import { signInAnonymously, User } from 'firebase/auth';
import { auth } from 'config/initAuth';
import { useAuthContext } from 'context/AuthContext';
import { graphQLFetcher } from 'api/fetchers';
import {
  PackageBooking,
  PackageBookingListResult,
  PackageBookingResult,
  PackageMoney,
  PackagePaymentOrderResult,
  packageBookingPaymentRecordQuery,
  packageBookingsClaimQuery,
  packageBookingsMoveQuery,
  packageBookingsQuery,
  packagePaymentOrderCreationQuery,
} from 'api/queries/packageTour';
import { authHeaders } from 'utils/authHeaders';
import { paymentOrderOutcome, pollDelay } from 'utils/packageBooking';
import { recordOutcome } from 'utils/packageMyBookings';
import { PACKAGE_TOURS_IN_HOUSE } from 'utils/packageToursSwitch';

// WHIT-483 round 3: package bookings in "My bookings" — the list, and paying
// a held booking or a balance (a payment order, the card, then op 9).

export const MOVED_EVENT = 'package-bookings-moved';

// `claimKey`: the booking a link named (?package=), claimed before the list
// is read so that a booking opened from its email is in it.
export const usePackageMyBookings = (
  enabled: boolean,
  claimKey: string | null = null,
) => {
  const { authUser, loading } = useAuthContext();
  const [result, setResult] = useState<{
    bookings: PackageBooking[] | null;
    error: string | null;
  }>({ bookings: null, error: null });
  const [reads, setReads] = useState(0);

  // Opened from a booking's emailed link in a browser where nobody is signed
  // in: an anonymous user is made first (as the booking page does), so the
  // booking can be claimed and paid here.
  useEffect(() => {
    if (enabled && claimKey && !loading && !authUser) {
      signInAnonymously(auth).catch(() => {
        setResult({ bookings: null, error: 'UNKNOWN' });
      });
    }
  }, [enabled, claimKey, loading, authUser]);

  useEffect(() => {
    if (!enabled || loading || !authUser) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeaders(authUser);
        if (claimKey) {
          // A link this user may not claim still lists their own bookings.
          await graphQLFetcher({
            query: packageBookingsClaimQuery,
            variables: { key: claimKey },
            headers,
          }).catch(() => null);
        }
        const response = await graphQLFetcher<{
          packageBookings: PackageBookingListResult;
        }>({
          query: packageBookingsQuery,
          headers,
        });
        if (cancelled) return;
        const answer = response.packageBookings;
        setResult(
          answer?.bookings
            ? { bookings: answer.bookings, error: null }
            : { bookings: null, error: answer?.error?.code || 'UNKNOWN' },
        );
      } catch {
        if (!cancelled) setResult({ bookings: null, error: 'UNKNOWN' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, loading, authUser, reads, claimKey]);

  const reload = useCallback(() => setReads(n => n + 1), []);
  // Bookings moved here after signing in arrive after the first read.
  useEffect(() => {
    window.addEventListener(MOVED_EVENT, reload);
    return () => window.removeEventListener(MOVED_EVENT, reload);
  }, [reload]);
  // Nobody signed in, not even anonymously: there is nothing to list.
  const empty = enabled && !loading && !authUser && !claimKey;
  return {
    bookings: empty ? [] : result.bookings,
    error: empty ? null : result.error,
    reload,
  };
};

// How long to keep asking whether a payment the card field reported has
// finished at the provider before telling the customer to look again later.
const RECORD_ATTEMPTS = 8;

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const usePackagePayment = () => {
  const { authUser } = useAuthContext();

  const request = useCallback(
    async <T>(query: string, variables: Record<string, any>) =>
      graphQLFetcher<T>({
        query,
        variables,
        headers: await authHeaders(authUser),
      }),
    [authUser],
  );

  // A payment order for this booking: for `amount` (a balance), or — with
  // none — for what the booking needs now. paymentOrderOutcome says what next.
  const startOrder = useCallback(
    async (
      bookingId: string,
      provider: string,
      amount: PackageMoney | null,
      email: string | null,
    ) => {
      try {
        const response = await request<{
          packagePaymentOrderCreation: PackagePaymentOrderResult;
        }>(packagePaymentOrderCreationQuery, {
          bookingId,
          paymentProvider: provider,
          amount,
          email: email || null,
        });
        return paymentOrderOutcome(response.packagePaymentOrderCreation);
      } catch {
        return { next: 'refused', code: 'PAYMENT_UNAVAILABLE' };
      }
    },
    [request],
  );

  // Tells taurus-api the card was paid. Asked again while the provider has
  // not finished; never a new payment.
  const record = useCallback(
    async (bookingId: string, paymentOrderId: string) => {
      for (let attempt = 0; attempt < RECORD_ATTEMPTS; attempt += 1) {
        let outcome;
        try {
          const response = await request<{
            packageBookingPaymentRecord: PackageBookingResult;
          }>(packageBookingPaymentRecordQuery, { bookingId, paymentOrderId });
          outcome = recordOutcome(response.packageBookingPaymentRecord);
        } catch {
          // The answer was lost: the same payment is reported again.
          outcome = { next: 'wait' };
        }
        if (outcome.next !== 'wait') return outcome;
        await wait(pollDelay(attempt));
      }
      return { next: 'failed', code: 'PAYMENT_PENDING' };
    },
    [request],
  );

  return { startOrder, record };
};

// Before signing in to an account in a browser that booked anonymously: the
// anonymous user's token, which later proves whose bookings to move. Null
// when nobody, or a real account, is signed in.
export const anonymousToken = async (user: User | null) => {
  if (!PACKAGE_TOURS_IN_HOUSE || !user || !user.isAnonymous) return null;
  try {
    return await user.getIdToken();
  } catch {
    return null;
  }
};

// After signing in: the anonymous user's package bookings become the
// account's, so they are in its "My bookings". Best effort: a failure leaves
// them where they were, and signing in is not held up.
export const movePackageBookings = async (token: string | null, user: User) => {
  if (!token) return;
  try {
    await graphQLFetcher({
      query: packageBookingsMoveQuery,
      variables: { anonymousToken: token },
      headers: await authHeaders(user),
    });
    window.dispatchEvent(new Event(MOVED_EVENT));
  } catch {
    // Nothing to do: see above.
  }
};
